import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePrinterStore } from "@/store/printerStore";
import { usePrinterConnection, tryAutoConnectPrinter, isThermalPrinterConfigured } from "@/hooks/usePrinterConnection";
import { ConnectionTypeSelector } from "@/components/common/ConnectionTypeSelector";
import UsbPrinterSetupWizard from "@/components/common/UsbPrinterSetupWizard";
import { pingPrintHelper, isHelperVersionOk, PRINT_HELPER_VERSION, printViaHelper, downloadPrintHelperInstaller } from "@/services/printHelper";
import { openCashDrawer } from "@/utils/thermalPrinterUtils";
import { isLaserOrPagePrinter, browserPrintTestPage } from "@/utils/laserPrinterUtils";
import { posApi } from "@/services/api";
import { CheckCircle2, Printer as PrinterIcon, AlertTriangle, Download } from "lucide-react";
import { Link } from "react-router-dom";

export default function PrinterSettingsPage() {
  const { t } = useTranslation();
  const {
    draft, isDirty, errorMsg,
    updateDraft, saveDraft, discardDraft, resetDefaults, setStatus, setError,
  } = usePrinterStore();

  const isLaser = isLaserOrPagePrinter(draft.windowsPrinterName, draft.paperWidth);

  const {
    connect, disconnect, testPrint,
    scanBluetooth,
    status: connStatus,
  } = usePrinterConnection();

  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [usbWizardOpen, setUsbWizardOpen] = useState(false);
  const [helperOnline, setHelperOnline] = useState<boolean | null>(null);
  const [installedVersion, setInstalledVersion] = useState<string | null>(null);
  const [helperNeedsUpdate, setHelperNeedsUpdate] = useState(false);
  const qc = useQueryClient();

  const { data: printReceiptAfterSale = true } = useQuery({
    queryKey: ["posSettings"],
    queryFn: async () => {
      const res = await posApi.getPosSettings();
      const settings = res.data.message as { print_receipt_after_sale?: boolean };
      return settings.print_receipt_after_sale !== false;
    },
    staleTime: 60_000,
  });

  const updateReceiptAfterSaleMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      posApi.updatePosSettings({ print_receipt_after_sale: enabled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["posSettings"] });
      void qc.invalidateQueries({ queryKey: ["profile"] });
    },
  });

  const usingHelperUsb =
    draft.connectionType === "usb" && draft.usePrintHelper !== false;

  // If an old WiFi preference was saved, flip to USB for this session draft
  useEffect(() => {
    if (draft.connectionType === "wifi") {
      updateDraft("connectionType", "usb");
    }
  }, [draft.connectionType, updateDraft]);

  useEffect(() => {
    void tryAutoConnectPrinter();
  }, []);

  useEffect(() => {
    if (!usingHelperUsb) return;
    void pingPrintHelper().then((ping) => {
      const ok = !!ping && isHelperVersionOk(ping.version);
      setHelperOnline(ok);
      setInstalledVersion(ping?.version || null);
      setHelperNeedsUpdate(!!ping && !isHelperVersionOk(ping.version));
    });
  }, [usingHelperUsb, usbWizardOpen]);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  const handleSave = () => {
    saveDraft();
    showToast("Settings saved.", true);
  };

  const handleScanBluetooth = async () => {
    setIsScanning(true);
    const ok = await scanBluetooth();
    setIsScanning(false);
    if (!ok) {
      const msg = usePrinterStore.getState().errorMsg;
      showToast(msg ?? "Bluetooth pairing cancelled or failed.", false);
    } else {
      showToast("Bluetooth device paired.", true);
    }
  };

  const handleTestConnect = async () => {
    if (usingHelperUsb) {
      setStatus("connecting");
      const ping = await pingPrintHelper();
      const ok = !!ping && isHelperVersionOk(ping.version);
      setHelperOnline(ok);
      if (!ping) {
        setStatus("error");
        setError("Sale Desk Print Helper is not running. Open the Add printer wizard to install/start it.");
        showToast("Print Helper offline.", false);
        return;
      }
      if (!isHelperVersionOk(ping.version)) {
        setStatus("error");
        setError(
          `Old Print Helper v${ping.version}. Close it in Task Manager and run v${PRINT_HELPER_VERSION}.`
        );
        showToast("Update Print Helper required.", false);
        return;
      }
      if (!draft.windowsPrinterName) {
        setStatus("error");
        setError("No Windows printer selected. Use Add printer wizard.");
        showToast("Select a printer first.", false);
        return;
      }
      setStatus("connected");
      setError(null);
      showToast(`Print Helper v${ping.version} connected.`, true);
      return;
    }
    const ok = await connect();
    const msg = usePrinterStore.getState().errorMsg;
    showToast(ok ? "Connected successfully!" : msg ?? "Connection failed.", ok);
  };

  const handleBrowserTestPrint = () => {
    browserPrintTestPage({
      header: draft.headerText || "Sale Desk",
      printerName: draft.windowsPrinterName || "Laser / Office Printer",
    });
  };

  const handleTestPrint = async () => {
    if (isLaser) {
      if (usingHelperUsb && draft.windowsPrinterName) {
        setStatus("connecting");
        try {
          const { buildA4TestImageBase64 } = await import("@/utils/laserPrinterUtils");
          const { printImageViaHelper } = await import("@/services/printHelper");
          const imgBase64 = await buildA4TestImageBase64({
            header: draft.headerText || "SALE DESK",
            printerName: draft.windowsPrinterName,
          });
          await printImageViaHelper(draft.windowsPrinterName, imgBase64);
          setStatus("connected");
          setError(null);
          showToast(`Direct A4 test print sent to ${draft.windowsPrinterName}!`, true);
          return true;
        } catch (e: any) {
          console.warn("Direct test print failed, falling back to browser dialog", e);
          handleBrowserTestPrint();
          showToast("Print Helper busy. Opened browser print preview fallback.", true);
          return true;
        }
      }
      handleBrowserTestPrint();
      showToast("Opening A4 test print dialog for your printer...", true);
      return true;
    }
    if (usingHelperUsb) {
      if (!draft.windowsPrinterName) {
        showToast("Select a printer via the wizard first.", false);
        return false;
      }
      setStatus("connecting");
      try {
        const lines = [
          draft.headerText?.trim() || "TEST PRINT",
          "------------------------------",
          "SALE DESK PRINTER TEST",
          `Time: ${new Date().toLocaleString()}`,
          "",
        ].join("\n") + "\n".repeat(Math.max(1, draft.feedLines));
        const enc = new TextEncoder().encode(lines);
        let bytes = enc;
        if (draft.autoCut) {
          const cut = new Uint8Array([0x1d, 0x56, 0x42, 0x00]);
          bytes = new Uint8Array(enc.length + cut.length);
          bytes.set(enc, 0);
          bytes.set(cut, enc.length);
        }
        await printViaHelper(draft.windowsPrinterName, bytes);
        setStatus("connected");
        setError(null);
        showToast("Test print sent.", true);
        return true;
      } catch (e: any) {
        setStatus("error");
        setError(e?.message || "Test print failed.");
        showToast(e?.message || "Test print failed.", false);
        return false;
      }
    }
    const ok = await testPrint();
    const msg = usePrinterStore.getState().errorMsg;
    showToast(ok ? "Test print sent." : msg ?? "Test print failed.", ok);
    return ok;
  };

  const handleTestDrawer = async () => {
    if (!isThermalPrinterConfigured(draft)) {
      showToast("Configure a printer first.", false);
      return;
    }
    setStatus("connecting");
    const ok = await openCashDrawer();
    if (ok) {
      setStatus("connected");
      setError(null);
      showToast(t('settings.drawerKickSent'), true);
    } else {
      setStatus("error");
      setError(t('settings.drawerKickFailed'));
      showToast(t('settings.drawerKickFailed'), false);
    }
  };

  const SELECT_CLS =
    "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-gray-900";
  const INPUT_CLS =
    "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-900";

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium text-gray-900">Printer settings</h1>
          <p className="text-sm text-gray-500 mt-0.5">USB (Print Helper) and Bluetooth · saved on this browser</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2.5 py-1 rounded-md border font-medium ${
            connStatus === "connected"  ? "bg-green-50 text-green-700 border-green-200" :
            connStatus === "connecting" ? "bg-amber-50 text-amber-700 border-amber-200" :
            connStatus === "error"      ? "bg-red-50 text-red-700 border-red-200"       :
                                          "bg-gray-50 text-gray-500 border-gray-200"
          }`}>
            {connStatus === "connected"  ? "Connected"    :
             connStatus === "connecting" ? "Connecting…"  :
             connStatus === "error"      ? "Error"        :
             (draft.connectionType === "bluetooth" && draft.bluetoothDeviceName)
               ? "Paired · not linked"
               : "Not connected"}
          </span>
          <span className={`text-xs px-2.5 py-1 rounded-md border font-medium ${
            isDirty
              ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-green-50 text-green-700 border-green-200"
          }`}>
            {isDirty ? "Unsaved" : "Saved"}
          </span>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`text-sm px-4 py-3 rounded-lg border ${
          toast.ok
            ? "bg-green-50 text-green-700 border-green-200"
            : "bg-red-50 text-red-700 border-red-200"
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Helper update alert banner */}
      {helperNeedsUpdate && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-amber-50 border border-amber-300 rounded-xl text-amber-950 shadow-xs">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-sm">Print Helper Update Available (v{PRINT_HELPER_VERSION})</h4>
              <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                You are currently running v{installedVersion || "1.2.0"}. Version v{PRINT_HELPER_VERSION} is required for direct silent A4/laser printing without browser preview popups.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => downloadPrintHelperInstaller()}
            className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm active:scale-95"
          >
            <Download className="w-4 h-4" />
            <span>Download Update v{PRINT_HELPER_VERSION}</span>
          </button>
        </div>
      )}

      {/* Connection type */}
      <ConnectionTypeSelector
        value={draft.connectionType}
        onChange={(t) => updateDraft("connectionType", t)}
        bluetoothDeviceName={draft.bluetoothDeviceName}
        onScanBluetooth={handleScanBluetooth}
        isScanning={isScanning}
      />

      {/* USB — Sale Desk Print Helper */}
      {draft.connectionType === "usb" && (
        <section className="border border-gray-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="text-sm font-medium text-gray-900">USB printer</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Uses Sale Desk Print Helper on this PC (Windows printer list)
              </p>
            </div>
            <button
              type="button"
              onClick={() => setUsbWizardOpen(true)}
              className="text-xs px-3 py-1.5 bg-gray-900 text-white rounded-lg hover:bg-gray-700 shrink-0"
            >
              + Add printer (wizard)
            </button>
          </div>
          {draft.windowsPrinterName ? (
            <div className="flex items-center gap-3 p-3.5 rounded-lg border border-green-200 bg-green-50">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-green-950 truncate">{draft.windowsPrinterName}</p>
                  {isLaser && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-green-200/70 text-green-900 border border-green-300">
                      Laser / A4 (Windows Print)
                    </span>
                  )}
                </div>
                <p className="text-xs text-green-700 mt-0.5">
                  {isLaser
                    ? "Prints invoices & test sheets via Windows printing engine"
                    : `Helper: ${helperOnline === null ? "…" : helperOnline ? "running (background)" : "not running"}`}
                </p>
              </div>
              <button
                type="button"
                onClick={handleTestPrint}
                disabled={connStatus === "connecting"}
                title="Print test page to this printer"
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-green-300 bg-white text-green-800 hover:bg-green-100 hover:border-green-400 shadow-sm transition-all disabled:opacity-50"
              >
                <PrinterIcon className="w-4 h-4 text-green-700 shrink-0" />
                <span>Test print</span>
              </button>
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-3 text-center">
              No USB printer linked yet. Open the wizard to download the helper and pick a printer.
            </p>
          )}
        </section>
      )}

      <UsbPrinterSetupWizard
        open={usbWizardOpen}
        onClose={() => setUsbWizardOpen(false)}
        windowsPrinterName={draft.windowsPrinterName}
        onSelectPrinter={(name) => {
          updateDraft("windowsPrinterName", name);
          updateDraft("usePrintHelper", true);
          updateDraft("connectionType", "usb");
        }}
        onSaveSettings={saveDraft}
        headerText={draft.headerText}
        feedLines={draft.feedLines}
        autoCut={draft.autoCut}
      />

      {/* Serial params — only if not using Print Helper */}
      {draft.connectionType === "usb" && draft.usePrintHelper === false && (
        <section className="border border-gray-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
            <h2 className="text-sm font-medium text-gray-900">Connection</h2>
            <span className="text-xs text-gray-400">Serial port parameters</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-gray-500">Baud rate</span>
              <select value={draft.baudRate} onChange={(e) => updateDraft("baudRate", Number(e.target.value))} className={SELECT_CLS}>
                {[9600, 19200, 38400, 57600, 115200].map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-gray-500">Data bits</span>
              <select value={draft.dataBits} onChange={(e) => updateDraft("dataBits", Number(e.target.value) as 7 | 8)} className={SELECT_CLS}>
                <option value={7}>7</option>
                <option value={8}>8</option>
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-gray-500">Stop bits</span>
              <select value={draft.stopBits} onChange={(e) => updateDraft("stopBits", Number(e.target.value) as 1 | 2)} className={SELECT_CLS}>
                <option value={1}>1</option>
                <option value={2}>2</option>
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-gray-500">Parity</span>
              <select value={draft.parity} onChange={(e) => updateDraft("parity", e.target.value as "none" | "even" | "odd")} className={SELECT_CLS}>
                <option value="none">None</option>
                <option value="even">Even</option>
                <option value="odd">Odd</option>
              </select>
            </label>
          </div>
        </section>
      )}

      {/* Paper & format */}
      <section className="border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
          <h2 className="text-sm font-medium text-gray-900">Paper & format</h2>
          <span className="text-xs text-gray-400">Receipt layout</span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-gray-500">Paper width</span>
            <select
              value={draft.paperWidth}
              onChange={(e) => {
                const val = e.target.value;
                updateDraft("paperWidth", val === "A4" ? "A4" : (Number(val) as 58 | 80));
              }}
              className={SELECT_CLS}
            >
              <option value={58}>58 mm</option>
              <option value={80}>80 mm</option>
              <option value="A4">A4</option>
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-gray-500">Print density</span>
            <select value={draft.density} onChange={(e) => updateDraft("density", Number(e.target.value) as 0 | 1 | 2)} className={SELECT_CLS}>
              <option value={0}>Light</option>
              <option value={1}>Normal</option>
              <option value={2}>Dark</option>
            </select>
          </label>
          <label className="flex flex-col gap-1.5 col-span-2">
            <span className="text-xs font-medium text-gray-500">Receipt header</span>
            <input type="text" value={draft.headerText} onChange={(e) => updateDraft("headerText", e.target.value)} placeholder="Store name" className={INPUT_CLS} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-gray-500">Feed lines after cut</span>
            <input type="number" min={0} max={10} value={draft.feedLines} onChange={(e) => updateDraft("feedLines", Number(e.target.value))} className={INPUT_CLS} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-gray-500">Character set</span>
            <select value={draft.charset} onChange={(e) => updateDraft("charset", e.target.value as "PC437" | "PC850" | "PC858")} className={SELECT_CLS}>
              <option value="PC437">PC437 (USA)</option>
              <option value="PC850">PC850 (Multilingual)</option>
              <option value="PC858">PC858 (Euro)</option>
            </select>
          </label>
        </div>
      </section>

      {/* Direct Silent Print for A4 / Laser */}
      {(draft.paperWidth === "A4" || isLaser) && (
        <section className="border border-emerald-200 bg-emerald-50/50 rounded-xl p-5 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-emerald-950">
                  Direct Silent Printing (A4 / Laser)
                </h3>
                <span className="text-[10px] bg-emerald-200/80 text-emerald-900 border border-emerald-300 px-2 py-0.5 rounded-full font-semibold">
                  Zero Print Window
                </span>
              </div>
              <p className="text-xs text-emerald-800 leading-relaxed max-w-md">
                A4 sales invoices and quotations are now spooled directly to your selected Windows printer via the background <strong>Sale Desk Print Helper</strong> with zero popups or browser preview windows.
              </p>
            </div>
            <button
              type="button"
              onClick={handleTestPrint}
              disabled={status === "connecting"}
              className="shrink-0 inline-flex items-center justify-center gap-2 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-sm transition-all hover:shadow active:scale-95 disabled:opacity-50"
            >
              <PrinterIcon className="w-4 h-4" />
              <span>Test Direct Print</span>
            </button>
          </div>
          <div className="text-[11px] text-emerald-900 bg-white/70 p-3 rounded-lg border border-emerald-200 space-y-1">
            <p className="font-semibold text-emerald-950">⚡ Effortless Operation:</p>
            <p className="text-emerald-800">
              When <strong>Sale Desk Print Helper</strong> is running and a Windows printer is selected above, clicking "Print" in POS or Sales Hub immediately sends the A4 document straight to your laser printer. No batch files or Chrome flags required!
            </p>
          </div>
        </section>
      )}

      {/* Behaviour */}
      <section className="border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 pb-3 border-b border-gray-100 mb-1">
          <h2 className="text-sm font-medium text-gray-900">Behaviour</h2>
        </div>
        {([
          ["autoPrintOnSale", "Silent print on sale complete", "Print thermal receipt automatically when a sale finishes (no Print click)"],
          ["autoCut",     "Auto-cut after print",  "Send cut command when job finishes"],
          ["autoConnect", "Auto-connect on load",  "Reconnect to last printer on startup"],
        ] as const).map(([key, label, sub]) => (
          <div key={key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-none">
            <div>
              <p className="text-sm text-gray-800">{label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
            </div>
            <button
              role="switch"
              aria-checked={draft[key] as boolean}
              onClick={() => updateDraft(key, !(draft[key] as boolean) as any)}
              className={`relative w-10 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 ${
                draft[key] ? "bg-gray-900" : "bg-gray-200"
              }`}
            >
              <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                draft[key] ? "translate-x-4" : "translate-x-0"
              }`} />
            </button>
          </div>
        ))}
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div>
            <p className="text-sm text-gray-800">{t('settings.printReceiptAfterSale')}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t('settings.printReceiptAfterSaleHelp')}</p>
          </div>
          <button
            role="switch"
            aria-checked={printReceiptAfterSale}
            disabled={updateReceiptAfterSaleMutation.isPending}
            onClick={() =>
              updateReceiptAfterSaleMutation.mutate(!printReceiptAfterSale)
            }
            className={`relative w-10 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 ${
              printReceiptAfterSale ? "bg-gray-900" : "bg-gray-200"
            }`}
          >
            <span
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                printReceiptAfterSale ? "translate-x-4" : "translate-x-0"
              }`}
            />
          </button>
        </div>
        {draft.autoPrintOnSale && !isThermalPrinterConfigured(draft) && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-2">
            Pair or configure a printer above before silent print can run.
          </p>
        )}
        {draft.autoPrintOnSale && !printReceiptAfterSale && (
          <p className="text-xs text-gray-500 mt-2">
            {t('settings.handsFreeCheckoutHint')}
          </p>
        )}
      </section>

      {/* Test connection */}
      <section className="border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-900">Test connection</p>
            <p className="text-xs text-gray-400 mt-0.5">Uses current settings draft and paired device selection</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {connStatus === "connected" && (
              <button onClick={disconnect} className="text-sm px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                Disconnect
              </button>
            )}
            <button
              onClick={handleTestConnect}
              disabled={connStatus === "connecting"}
              className="text-sm px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
            >
              {connStatus === "connecting" ? "Connecting…" : "Test connection"}
            </button>
            <button
              onClick={handleTestPrint}
              disabled={connStatus === "connecting"}
              className="text-sm px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              {isLaser ? "Print test page" : "Print test receipt"}
            </button>
            <button
              onClick={handleTestDrawer}
              disabled={connStatus === "connecting"}
              className="text-sm px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              {t('settings.testCashDrawer')}
            </button>
          </div>
        </div>
        {errorMsg && (
          <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {errorMsg}
          </p>
        )}
      </section>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2 pb-8">
        <button onClick={() => { resetDefaults(); showToast("Reset to defaults.", true); }}
          className="text-sm text-gray-400 hover:text-gray-600 transition-colors">
          Reset to defaults
        </button>
        <div className="flex gap-2">
          <button onClick={discardDraft} disabled={!isDirty}
            className="text-sm px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
            Discard
          </button>
          <button onClick={handleSave} disabled={!isDirty}
            className="text-sm px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">
            Save settings
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500 pt-2">
        Looking for weighing scale?{" "}
        <Link to="/settings?tab=scale" className="underline text-gray-700">
          Open Weighing Scale settings
        </Link>
      </p>
    </div>
  );
}
